import React from "react";
import { L as Link, useActiveLocale } from "../lib/i18n";
import { fmtCHF, fmtKM } from "../lib/constants";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { Gauge, Calendar, Fuel, Cog, MapPin, ShieldCheck, Sparkles, BadgeCheck, Crown,
  FileCheck2, BookCheck, BatteryFull, BatteryCharging, BatteryMedium, BatteryLow } from "lucide-react";
import { DealerBadge } from "./DealerBadge";
import { CompareToggleButton } from "./CompareToggleButton";

const FALLBACK = "https://images.unsplash.com/photo-1761229170508-f4791c297af8?w=1000&q=70&auto=format&fit=crop";

export default function CarCard({ car, similarityBadge }) {
  const { t } = useLang();
  const img = car.images?.[0] ? api.fileUrl(car.images[0]) : FALLBACK;
  const isNew = car.condition === "neu";
  // Featured only when the boost has not expired yet
  const isFeatured = !!(car.featured && car.featured_until && new Date(car.featured_until) > new Date());
  // Phase 56 — Premium has its own (amber) badge with priority over Featured.
  const isPremium = !!(car.premium && car.premium_until && new Date(car.premium_until) > new Date());
  const dealer = car.seller_type === "dealer";
  const sellerDealer = car.seller_dealer;  // backend-enriched dealer info
  const warranty = (car.warranty_months || 0) > 0;

  return (
    <Link
      to={`/listings/${car.id}`}
      data-testid={`car-card-${car.id}`}
      data-featured={isFeatured ? "true" : "false"}
      data-premium={isPremium ? "true" : "false"}
      className={`group ch-card ch-glow relative block overflow-hidden rounded-2xl ${
        isPremium
          ? "ring-1 ring-amber-400/60 shadow-[0_0_28px_-6px_rgba(251,191,36,0.55)]"
          : isFeatured
            ? "ring-1 ring-[var(--ch-red)]/50 shadow-[0_0_24px_-8px_rgba(220,38,38,0.45)]"
            : ""
      }`}
    >
      {/* Phase 56 — Premium accent line (amber, takes precedence). */}
      {isPremium ? (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-1 bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
      ) : isFeatured && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-1 bg-gradient-to-r from-transparent via-[var(--ch-red)] to-transparent" />
      )}
      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden bg-[var(--ch-bg-2)]">
        <img
          src={img}
          alt={car.title}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0d12]/85 via-[#0a0d12]/10 to-transparent" />

        {/* Top-left badges */}
        <div className="absolute left-3 top-3 flex flex-col gap-1.5">
          {similarityBadge}
          {isPremium && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-black shadow-lg" data-testid={`premium-badge-card-${car.id}`}>
              <Crown className="h-3 w-3" /> Premium
            </span>
          )}
          {!isPremium && isFeatured && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--ch-red)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg">
              <Sparkles className="h-3 w-3" /> {t.card.featured}
            </span>
          )}
          {isNew && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
              {t.card.new}
            </span>
          )}
        </div>

        {/* Top-right trust */}
        <div className="absolute right-3 top-3 flex flex-col items-end gap-1.5">
          <CompareToggleButton listingId={car.id} />
          {sellerDealer ? (
            <DealerBadge dealer={sellerDealer} />
          ) : dealer && (
            <span className="inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-200 backdrop-blur-md">
              <BadgeCheck className="h-3 w-3 text-sky-400" /> Pro
            </span>
          )}
          {warranty && (
            <span className="inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-200 backdrop-blur-md">
              <ShieldCheck className="h-3 w-3 text-emerald-400" /> {car.warranty_months}M
            </span>
          )}
        </div>

        {/* Bottom price overlay */}
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-300">{car.brand}</p>
            <h3 className="mt-1 font-display text-lg font-semibold leading-tight text-white line-clamp-2">{car.title}</h3>
          </div>
          <div className="flex-shrink-0 rounded-lg bg-[var(--ch-red)]/95 px-3 py-1.5 font-display text-sm font-bold text-white shadow-lg">
            {fmtCHF(car.price_chf)}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-2 px-4 py-4 text-xs text-zinc-300">
        <Stat icon={<Calendar className="h-3.5 w-3.5" />} value={car.year} />
        <Stat icon={<Gauge className="h-3.5 w-3.5" />} value={`${fmtKM(car.mileage_km)}`} />
        <Stat icon={<Fuel className="h-3.5 w-3.5" />} value={car.fuel} cap />
        <Stat icon={<Cog className="h-3.5 w-3.5" />} value={car.transmission} cap />
      </div>

      {/* Phase 70 — Mini Trust Pills (max 3, priority-ordered) */}
      <MiniTrustPills car={car} warrantyMonthsShown={warranty} />

      {car.location && (
        <div className="flex items-center gap-1.5 border-t border-[var(--ch-line-soft)] px-4 py-2.5 text-[11px] text-zinc-500">
          <MapPin className="h-3 w-3" /> {car.location}
        </div>
      )}
    </Link>
  );
}

function Stat({ icon, value, cap }) {
  return (
    <div className={`flex items-center gap-1.5 ${cap ? "capitalize" : ""}`}>
      <span className="text-zinc-500">{icon}</span>
      <span className="truncate">{value}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Phase 70 — Mini Trust Pills (compact, max 3, priority-ordered).
 * Surfaces the highest-impact Phase 68 attributes inline on the card so
 * users see trust signals before clicking.
 *
 * Priority:
 *   1. Battery SOH (EV/hybrid only, color-graded)
 *   2. Unfallfrei (accident_free === true)
 *   3. Ab MFK (ab_mfk === true)
 *   4. Garantie (warranty_available === true, only when no warranty-months
 *      overlay is already shown on the image — avoid duplication)
 *   5. Service-Heft (service_book === true)
 * ──────────────────────────────────────────────────────────────────── */
const MINI_TRUST_LABELS = {
  de: { accident_free: "Unfallfrei", ab_mfk: "Ab MFK",   warranty: "Garantie", service_book: "Service-Heft" },
  fr: { accident_free: "Sans accident", ab_mfk: "OFROU récent", warranty: "Garantie", service_book: "Carnet" },
  it: { accident_free: "Senza incidenti", ab_mfk: "Collaudo recente", warranty: "Garanzia", service_book: "Tagliandi" },
  en: { accident_free: "Accident-free", ab_mfk: "Fresh MFK", warranty: "Warranty", service_book: "Service book" },
};

function batteryPill(pct) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  let tone, Icon;
  if (p >= 90)      { tone = "emerald";  Icon = BatteryFull; }
  else if (p >= 75) { tone = "emerald2"; Icon = BatteryCharging; }
  else if (p >= 60) { tone = "amber";    Icon = BatteryMedium; }
  else              { tone = "orange";   Icon = BatteryLow; }
  const toneClass = {
    emerald:  "border-emerald-500/40 bg-emerald-500/15 text-emerald-200",
    emerald2: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
    amber:    "border-amber-500/40 bg-amber-500/15 text-amber-200",
    orange:   "border-orange-500/40 bg-orange-500/15 text-orange-200",
  }[tone];
  return { Icon, label: `${p}% SOH`, toneClass, key: "battery" };
}

function MiniTrustPills({ car, warrantyMonthsShown }) {
  const locale = useActiveLocale();
  const labels = MINI_TRUST_LABELS[locale] || MINI_TRUST_LABELS.de;

  const isEv = ["elektro", "electric", "hybrid"].includes(String(car.fuel || "").toLowerCase());
  const pills = [];

  if (isEv && Number(car.battery_health_pct) > 0) {
    pills.push(batteryPill(car.battery_health_pct));
  }
  if (car.accident_free === true) {
    pills.push({ Icon: ShieldCheck, label: labels.accident_free,
      toneClass: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200", key: "accident_free" });
  }
  if (car.ab_mfk === true) {
    pills.push({ Icon: FileCheck2, label: labels.ab_mfk,
      toneClass: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200", key: "ab_mfk" });
  }
  if (car.warranty_available === true && !warrantyMonthsShown) {
    pills.push({ Icon: BadgeCheck, label: labels.warranty,
      toneClass: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200", key: "warranty" });
  }
  if (car.service_book === true) {
    pills.push({ Icon: BookCheck, label: labels.service_book,
      toneClass: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200", key: "service_book" });
  }

  if (pills.length === 0) return null;
  const visible = pills.slice(0, 3);

  return (
    <div
      className="flex flex-wrap gap-1.5 px-4 pb-3"
      data-testid={`mini-trust-pills-${car.id}`}
    >
      {visible.map(({ Icon, label, toneClass, key }) => (
        <span
          key={key}
          data-testid={`mini-trust-pill-${key}`}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium leading-tight ${toneClass}`}
        >
          <Icon className="h-3 w-3" />
          {label}
        </span>
      ))}
    </div>
  );
}
